import classNames from 'classnames';
import AnimatedImage from 'core-lib/components/AnimatedImage';
import ShimmerImage from 'core-lib/components/ShimmerImage';
import { useComponentInViewport } from 'core-lib/hooks/useComponentInViewport';
import formatCurrency from 'core-lib/utils/currency';
import { formatFuelBalance } from 'core-lib/utils/fuel';
import { getImageKitUrl } from 'core-lib/utils/imageKit';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import Icon from 'ui-v2/components/Icon/Icon';
import { Typography } from 'ui-v2/components/Typography';
import useStoreItemClick from '../../../hooks/useStoreItemClick';
import { LayoutPropType } from '../../../types/layout';
import {
    trackProductViewedStoreV2,
    trackSectionViewedStoreV2,
} from '../../../utils/events';
import SavingPopup from '../../Popups/SavingPopup';
import s from './CurvedCarousel.module.scss';

const Swiper = dynamic(() => import('core-lib/components/Swiper'), {
  ssr: false,
});

export const CurvedCarousel = ({ data }: LayoutPropType) => {
  const { handleItemClickWithDelegation } = useStoreItemClick();
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [showSavingPopup, setShowSavingPopup] = useState<boolean>(false);

  ////const
  const loyaltyPoints = data?.data?.[activeIndex]?.loyaltyPoints;
  const loyaltyPrice = data?.data?.[activeIndex]?.loyaltyPrice;
  //for click
  const CARDID = data?.layoutType;
  //for event
  const rootRef = useRef();
  const isEventFired = useRef(false);
  const inViewPort = useComponentInViewport(rootRef, {
    threshold: 0.5,
  });

  const swiperRef = useRef({});

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      setLoading(false);
    }, 90);

    return () => clearTimeout(timer); // Cleanup timer on unmount
  }, []);

  useEffect(() => {
    if (inViewPort) {
      if (!isEventFired.current) {
        trackSectionViewedStoreV2(data?.title);
        isEventFired.current = true;
      }
      const product = data?.data?.[activeIndex];
      if (product && !swiperRef.current[product?.productId]) {
        trackProductViewedStoreV2('deals_of_the_day', product?.productId, {
          description: product?.eventType,
        });
        swiperRef.current[product?.productId] = true;
      }
    }
  }, [data, activeIndex, inViewPort]);

  return (
    <div
      className={s.wrapper}
      ref={rootRef}
      onClick={(e) => {
        handleItemClickWithDelegation(
          e,
          CARDID,
          data?.entityType,
          data?.data,
          'deals_of_the_day'
        );
      }}
    >
      <div className={s.top}>
        <AnimatedImage src={data?.metaData?.headingImage} className={s.logo} />
      </div>
      <Swiper
        onSlideChange={(el) => {
          setActiveIndex(el.realIndex);
        }}
        swiperModules={['Pagination', 'Autoplay']}
        spaceBetween={20}
        slidesPerView={1.2}
        centeredSlides={true}
        pagination={{
          el: '.swiper-pagination',
          clickable: true,
        }}
        effect="fade"
        fadeEffect={{
          crossFade: true,
        }}
        speed={500}
        loop={true}
        autoplay={{
          delay: 2500,
          disableOnInteraction: false,
        }}
        slides={data?.data?.map((item, index) => (
          <div
            key={`${item?.productId}-$-${index}`}
            className={s.item}
            id={CARDID}
            data-index={index}
          >
            <ShimmerImage
              className={classNames('cmn_touchable_area', s.item)}
              src={getImageKitUrl({
                imageUrl: item?.coverImage,
                product: 'ImageKit',
                width: 700,
              })}
              loading="eager"
            />
          </div>
        ))}
      />
      <div className={s.bottom}>
        <Typography
          label={data?.data?.[activeIndex]?.title}
          variant="label_3_medium"
          className={s.title}
        />
        <div className={s.price}>
          <Typography
            label={formatCurrency(data?.data?.[activeIndex]?.discountedPrice)}
            variant="label_3_bold"
          />
          <Typography
            label={formatCurrency(data?.data?.[activeIndex]?.price)}
            variant="label_3_regular"
            className={s.lineThrough}
            color="tertiary"
          />
        </div>
        {loyaltyPoints > 0 && loyaltyPrice >= 0 && (
          <div className={s.mlPrice}>
            <Typography
              variant="label_4_bold"
              label={`${formatCurrency(loyaltyPrice)} using `}
              color="offer"
            />
            <AnimatedImage
              src="https://parkplus-bkt-img.parkplus.io/production/team/public/FS_20240104160730062768.png"
              className={s.drop}
              alt='mlDrop'
            />
            <Typography
              variant="label_4_bold"
              label={formatFuelBalance(loyaltyPoints)}
              color="offer"
            />
            <div
              onClick={() => {
                setShowSavingPopup(true);
              }}
              className={s.info}
            >
              <Icon name="InfoCircleRegular" width={20} height={20} color='secondary' />
            </div>
          </div>
        )}
      </div>
      <SavingPopup
        show={showSavingPopup}
        onClose={() => {
          setShowSavingPopup(false);
        }}
        saved={
          data?.data?.[activeIndex].discountedPrice -
          data?.data?.[activeIndex]?.loyaltyPrice
        }
        points={data?.data?.[activeIndex]?.loyaltyPoints}
      />
      {loading && (
        <div className={s.loader}>
          <div className={s.top} />
          <div className={classNames(s.image, 'cmn_shimmer')} />
          <div className={s.bottom} />
        </div>
      )}
    </div>
  );
};
